import {
  Snackbar,
  Alert,
  Card,
  CircularProgress,
  Backdrop,
  Icon,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Tooltip,
} from "@mui/material";

import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";

import MDBox from "theme/components/MDBox";
import MDTypography from "theme/components/MDTypography";
import DashboardLayout from "app/LayoutContainers/DashboardLayout";
import DashboardNavbar from "app/Navbars/DashboardNavbar";
import Footer from "theme/Footer";
import usePageTitle from "app/pageTitle";

import MDInput from "theme/components/MDInput";
import MDButton from "theme/components/MDButton";
import DataTable from "theme/Tables/DataTable";
import axios from "axios.js";
import { useAwaitDialog } from "app/components/status_dialog";

function TerritoriesPage() {
  const pageTitle = "Territories";
  usePageTitle(pageTitle);

  const navigate = useNavigate();

  const statusDialog = useAwaitDialog();

  const [showLoading, setShowLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const [errorMsg, setErrMsg] = useState("");
  const [severity, setSeverity] = useState("");

  const handleClose = () => {
    setOpen(false);
    setErrMsg("");
  };

  const [refreshPage, setRefreshPage] = useState(false);
  const [pageLoaded, setPageLoaded] = useState(false);

  const [territories, setTerritories] = useState([]);

  const [title, setTitle] = useState(null);
  const [openCreateDialog, setOpenCreateDialog] = useState(false);

  async function deleteTerritory(ref) {
    if (!window.confirm("Delete this territory?")) {
      return;
    }

    setShowLoading(true);

    await axios
      .get(`/portal/territories/territories/${ref}/delete.json`)
      .then((result) => {
        setShowLoading(false);

        if (result.data.status === "success") {
          setSeverity("success");
          setErrMsg(result.data.data.message);
          setOpen(true);

          setRefreshPage(!refreshPage);
        } else {
          setSeverity("error");
          setErrMsg(result.data.data.message);
          setOpen(true);
        }
      })
      .catch(() => {
        setShowLoading(false);
        setSeverity("error");

        setErrMsg(
          `Unable to complete request. ${
            navigator.onLine ? "" : "Make sure you have an active internet connection"
          }`
        );

        setOpen(true);
      });
  }

  const territoryTableColumns = [
    {
      Header: "Name",
      accessor: "name",
      align: "left",
    },
    {
      Header: "Actions",
      accessor: "actions",
      align: "right",
    },
  ];

  const buildRows = (data) => {
    const rows = [];

    data.map((row) => {
      rows.push({
        name: row.title,

        actions: (
          <MDBox mt={1} variant="button" display="flex" gap={0.5}>
            <Tooltip title="Manage Territory">
              <MDButton
                size="small"
                variant="outlined"
                color="dark"
                iconOnly
                onClick={() => {
                  navigate(`/geo/territories/${row.reference}`);
                }}
              >
                <Icon sx={{ cursor: "pointer" }} fontSize="small">
                  settings
                </Icon>
              </MDButton>
            </Tooltip>

            <Tooltip title="Delete Territory">
              <MDButton
                size="small"
                variant="outlined"
                color="error"
                iconOnly
                onClick={() => {
                  deleteTerritory(row.reference);
                }}
              >
                <Icon sx={{ cursor: "pointer" }} fontSize="small">
                  delete
                </Icon>
              </MDButton>
            </Tooltip>
          </MDBox>
        ),
      });

      return null;
    });

    return rows;
  };

  useEffect(() => {
    const fetchData = async () => {
      setShowLoading(true);

      await axios
        .get(`/portal/territories/territories.json`)
        .then((result) => {
          setShowLoading(false);

          if (result.data.status === "success") {
            const rows = buildRows(result.data.data.territories);

            setTerritories(rows);
            setPageLoaded(true);
          } else {
            setSeverity("error");
            setErrMsg(result.data.data.message);
            setOpen(true);
          }
        })
        .catch(() => {
          setShowLoading(false);
          setSeverity("error");

          setErrMsg(
            `Unable to complete request. ${
              navigator.onLine ? "" : "Make sure you have an active internet connection"
            }`
          );

          setOpen(true);
        });
    };

    fetchData();
  }, [refreshPage]);


  async function createTerritory() {
    if (title === null || title.trim() === "") {
      setSeverity("error");
      setErrMsg("Territory name is required.");
      setOpen(true);

      return;
    }

    setOpenCreateDialog(false);
    setShowLoading(true);

    const json = {
      title: `${title}`,
    };

    await axios
      .post("portal/territories/territories/create.json", json)
      .then(async (result) => {
        setShowLoading(false);

        if (result.data.status === "success") {
          setSeverity("success");
          setErrMsg(result.data.data.message);
          setOpen(true);

          await statusDialog.show(result.data.data.message, "success", "Success!");

          setTitle(null);

          navigate(`/geo/territories/${result.data.data.reference}`);
        } else {
          setSeverity("error");
          setErrMsg(result.data.data.message);
          setOpen(true);
        }
      })
      .catch(() => {
        setShowLoading(false);
        setSeverity("error");

        setErrMsg(
          `Unable to complete request. ${
            navigator.onLine ? "" : "Make sure you have an active internet connection"
          }`
        );

        setOpen(true);
      });
  }

  return (
    <DashboardLayout>
      <DashboardNavbar />

      <Backdrop
        sx={{
          color: "#fff",
          zIndex: (theme) => theme.zIndex.drawer + 1,
        }}
        open={showLoading}
        onClick={handleClose}
      >
        <CircularProgress color="inherit" />
      </Backdrop>

      <Snackbar
        open={open}
        autoHideDuration={6000}
        onClose={handleClose}
        anchorOrigin={{
          vertical: "top",
          horizontal: "center",
        }}
      >
        <Alert onClose={handleClose} severity={severity} sx={{ width: "100%" }}>
          {errorMsg}
        </Alert>
      </Snackbar>

      <Dialog open={openCreateDialog} fullWidth maxWidth="xs">
        <DialogTitle>
          <MDBox>New Territory</MDBox>
        </DialogTitle>

        <DialogContent>
          <MDBox mt={0} mb={3}>
            <MDInput
              value={title || ""}
              name="title"
              margin="dense"
              label="Territory Name"
              type="text"
              fullWidth
              variant="outlined"
              onChange={(e) => setTitle(e.target.value)}
            />
          </MDBox>
        </DialogContent>

        <DialogActions>
          <MDButton
            onClick={() => {
              setOpenCreateDialog(false);
            }}
            color="dark"
            variant="outlined"
          >
            Cancel
          </MDButton>

          <MDButton
            color="dark"
            variant="contained"
            onClick={() => {
              createTerritory();
            }}
          >
            Create
          </MDButton>
        </DialogActions>
      </Dialog>

      <MDBox pt={3} pb={3}>
        {pageLoaded && (
          <MDBox>
            <Card
              sx={{
                height: "100%",
                marginTop: "0px",
              }}
            >
              <MDBox p={2}>
                <MDBox align="center">
                  <MDBox>
                    <MDTypography variant="h6" fontSize={48}>
                      <Icon>public</Icon>
                    </MDTypography>

                    <MDTypography variant="h6" fontWeight="light" fontSize={16}>
                      Define geographic territories for your field operations and control where your
                      team can operate.
                    </MDTypography>
                  </MDBox>

                  <MDBox mt={3} mb={3}>
                    <MDButton
                      variant="contained"
                      color="dark"
                      onClick={() => {
                        setTitle(null);
                        setOpenCreateDialog(true);
                      }}
                      startIcon={<Icon>add</Icon>}
                    >
                      Create Territory
                    </MDButton>
                  </MDBox>
                </MDBox>
              </MDBox>
            </Card>

            {territories.length > 0 && (
              <MDBox mt={5}>
                <Card
                  sx={{
                    height: "100%",
                    marginTop: "0px",
                  }}
                >
                  <MDBox>
                    <MDBox p={2}>
                      <MDBox display="flex" justifyContent="space-between" alignItems="center">
                        <MDBox>
                          <MDTypography variant="h6" fontWeight="medium" textTransform="capitalize">
                            Territories
                          </MDTypography>
                        </MDBox>
                      </MDBox>
                    </MDBox>

                    <MDBox mt={0}>
                      <DataTable
                        table={{
                          columns: territoryTableColumns,
                          rows: territories,
                        }}
                        isSorted
                        canSearch={false}
                        entriesPerPage={false}
                        showTotalEntries
                        buttons
                        noEndBorder
                      />
                    </MDBox>
                  </MDBox>
                </Card>
              </MDBox>
            )}
          </MDBox>
        )}
      </MDBox>

      <Footer />
    </DashboardLayout>
  );
}

export default TerritoriesPage;
